import type { SkillLevelDto } from '../api/skills';
import { AnticipationRing } from './AnticipationRing';

export type SkillHeaderProps = { skill: SkillLevelDto };

/**
 * Progress through the current level, 0..1. `xp` is progress INTO `level` and `xpToNext` the
 * whole step (the server's `SkillLevelDto` follows the companion `XpService` convention), so the
 * fraction is `xp / xpToNext` — NOT `xp / (xp + xpToNext)`, which Task 16's real-browser run
 * caught drawing a 31 % ring for a skill 6 xp into a 53-xp level. Full at the cap (`xpToNext` 0).
 * Exported so the Skills sheet, the desk's skill nav and this card draw ONE arc, not three.
 */
export function levelFraction(skill: Pick<SkillLevelDto, 'xp' | 'xpToNext'>): number {
  if (skill.xpToNext <= 0) return 1;
  return Math.min(1, Math.max(0, skill.xp / skill.xpToNext));
}

/**
 * The skill header card (skill-craft UI polish, spec §3.4): a level ring at `xp / xpToNext`
 * (`levelFraction`) with the level inside it, `Name · Level n`, then `xp / xpToNext xp` and the two multipliers that
 * matter for this skill's kind — `gathers ×y.yy · speed ×s.ss` for a gather skill, `preserves p % ·
 * speed ×s.ss` for a process one.
 *
 * Every figure is a DTO field. `yieldMult`/`speedMult`/`preserveChance` are the server's own
 * `SkillMath` for `level`; there is deliberately no client-side curve to re-derive them from, so
 * a future retune of the square curve changes this card without a web deploy.
 */
export function SkillHeader({ skill }: SkillHeaderProps) {
  const fraction = levelFraction(skill);
  const speed = `speed ×${skill.speedMult.toFixed(2)}`;
  const mults = skill.kind === 'process'
    ? `preserves ${Math.round(skill.preserveChance * 100)} % · ${speed}`
    : `gathers ×${skill.yieldMult.toFixed(2)} · ${speed}`;
  return (
    <div className="skill-head">
      <div className="skill-ring">
        <AnticipationRing fraction={fraction} size="sm" />
        <span className="skill-ring-inner">{skill.level}</span>
      </div>
      <div className="skill-head-copy">
        <h2 className="skill-head-name">{`${skill.name} · Level ${skill.level}`}</h2>
        <p className="skill-head-meta">
          <b>{skill.xp}</b>{` / ${skill.xpToNext} xp · ${mults}`}
        </p>
      </div>
    </div>
  );
}
